import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-paper';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';

import { useGetAddress, useGetCities, useGetPlaceById } from '@/api/hooks/meHooks';
import { ManualGooglePlaceData } from '@/lib/validations/ManualGooglePlaceDetailSchema';
import { AddressComponent } from '@/utils/addressFromAddressComponents';
import { addressFromString } from '@/utils/addressFromString';
import { getManualPlaceDetail } from '@/utils/getManualPlaceDetail';
import { i18n } from '@/utils/i18n';

import Text from '../Text';
import TextInputAutocomplete, { PredictionType } from './TextIputAutocomplete';
import ManualGooglePlaceForm from '../forms/ManualGooglePlaceForm';

export interface GooglePlaceDetail {
  address1: string;
  address2: string;
  city: string;
  country: string;
  formattedAddress: string;
  lat: number;
  lng: number;
  postalCode: string;
  region: string;
  addressComponents: AddressComponent[];
}

interface GoogleAutoCompleteProps {
  initialValue?: string;
  type: 'city' | 'address';
  placeholder?: string;
  leadingIcon?: React.ReactNode;
  traillingIcon?: React.ReactNode;
  variant?: 'default' | 'rounded_with_shadow' | 'gray_borders';
  showError?: boolean;
  label?: string;
  predictionsScrollEnabled?: boolean;
  showNoResult?: boolean;
  showManualEntry?: boolean;
  error?: FieldError;
  onChange: (placeDetail?: GooglePlaceDetail) => void;
}

const GoogleAutoComplete = ({
  initialValue,
  type,
  placeholder,
  leadingIcon,
  traillingIcon,
  variant = 'default',
  showError = true,
  predictionsScrollEnabled = true,
  showNoResult = true,
  showManualEntry = false,
  label,
  error,
  onChange
}: GoogleAutoCompleteProps) => {
  const { mutateAsync: getAddress } = useGetAddress();
  const { mutateAsync: getCity } = useGetCities();
  const { mutateAsync: getPlaceById } = useGetPlaceById();
  const [search, setSearch] = useState({ term: '', fetchPredictions: false });
  const [initialManualData, setInitialManualData] = useState<ManualGooglePlaceData>();
  const [showPredictions, setShowPredictions] = useState(false);
  const [predictions, setPredictions] = useState<PredictionType[]>([]);
  const [manualEntryMode, setManualEntryMode] = useState(false);

  const onChangeText = async () => {
    if (search.term === undefined) return;
    if (search.term.trim() === '') return;
    if (!search.fetchPredictions) return;
    if (type === 'address') {
      getAddress(
        { address: search.term },
        {
          onSuccess: (result) => {
            const { suggestions } = result;
            const prediction = suggestions?.map(
              (suggestion: { placePrediction: { placeId: string; text: { text: string } } }) => {
                return {
                  value: suggestion.placePrediction.placeId,
                  label: suggestion.placePrediction.text.text
                };
              }
            );
            setPredictions(prediction);
            setShowPredictions(true);
          },
          onError: (e) => {
            console.error(e);
          }
        }
      );
    } else {
      getCity(
        { city: search.term },
        {
          onSuccess: (result) => {
            const { suggestions } = result;
            const prediction = suggestions?.map(
              (suggestion: { placePrediction: { placeId: string; text: { text: string } } }) => {
                return {
                  value: suggestion.placePrediction.placeId,
                  label: suggestion.placePrediction.text.text
                };
              }
            );
            setPredictions(prediction);
            setShowPredictions(true);
          },
          onError: (e) => {
            console.error(e);
          }
        }
      );
    }
  };

  const onPredictionTapped = async ({ value, label }: PredictionType) => {
    await getPlaceById(
      { placeId: value },
      {
        onSuccess: (result: {
          data: { address: GooglePlaceDetail; adrAddress: string; addressComponents: AddressComponent[] };
        }) => {
          onChange({ ...result.data.address, addressComponents: result.data.addressComponents });
          setSearch({ term: label, fetchPredictions: false });
          setShowPredictions(false);
          setInitialManualData(undefined);
        },
        onError: (e) => {
          console.error(e);
        }
      }
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useMemo(() => debounce(() => onChangeText(), 400), [search.term]);

  useEffect(() => {
    debounced();
    return () => {
      debounced.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.term]);

  useEffect(() => {
    if (initialValue) {
      setSearch({ term: initialValue, fetchPredictions: false });
      const parsedFromString = addressFromString(initialValue);
      setInitialManualData({
        address1: parsedFromString?.address ?? '',
        city: parsedFromString?.city ?? '',
        region: parsedFromString?.state ?? '',
        postalCode: parsedFromString?.postal_code ?? '',
        country: 'USA'
      });
    }
  }, [initialValue]);

  // Manual or autocomplete entry animation
  const AUTOCOMPLETE_HEIGHT = variant === 'gray_borders' ? 38 : 56;
  const AMOUNT_OF_INPUTS = 5;
  // TextInput height * amount of inputs + gap * amount of inputs - last input
  const MANUAL_HEIGHT = AUTOCOMPLETE_HEIGHT * AMOUNT_OF_INPUTS + 5 * (AMOUNT_OF_INPUTS - 1);

  const animationProgress = useSharedValue(manualEntryMode ? 1 : 0);

  useEffect(() => {
    animationProgress.value = withTiming(manualEntryMode ? 1 : 0, { duration: 300 });
  }, [animationProgress, manualEntryMode]);

  const containerHeight = useDerivedValue(() => {
    return interpolate(animationProgress.value, [0, 1], [AUTOCOMPLETE_HEIGHT, MANUAL_HEIGHT]);
  });

  const containerStyle = useAnimatedStyle(() => ({
    flexDirection: 'column',
    minHeight: containerHeight.value,
    width: '100%',
    overflow: animationProgress.value === 0 ? 'visible' : 'hidden',
    zIndex: 999
  }));

  const autocompleteStyle = useAnimatedStyle(() => ({
    width: '100%',
    height: containerHeight.value,
    opacity: interpolate(animationProgress.value, [0, 1], [1, 0]),
    transform: [{ translateY: interpolate(animationProgress.value, [0, 1], [0, -50]) }],
    zIndex: 999,
    display: animationProgress.value < 0.5 ? 'flex' : 'none'
  }));

  const manualFormStyle = useAnimatedStyle(() => ({
    width: '100%',
    opacity: animationProgress.value,
    transform: [{ translateY: interpolate(animationProgress.value, [0, 1], [50, 0]) }],
    display: animationProgress.value > 0.5 ? 'flex' : 'none'
  }));

  return (
    <View>
      {label && (
        <Text fontWeight="700" style={styles.label}>
          {label}
        </Text>
      )}
      <Animated.View style={containerStyle}>
        <Animated.View style={autocompleteStyle}>
          <TextInputAutocomplete
            value={search.term}
            placeholder={placeholder}
            onChangeText={(text) => {
              setSearch({ term: text, fetchPredictions: true });
              if (text.length === 0) {
                setShowPredictions(false);
                return;
              }
            }}
            leadingIcon={leadingIcon}
            traillingIcon={traillingIcon}
            variant={variant}
            predictions={predictions}
            showPredictions={showPredictions}
            onPredictionTapped={({ value, label }) => {
              onPredictionTapped({ value, label });
            }}
            predictionsScrollEnabled={predictionsScrollEnabled}
            showNoResult={showNoResult}
            extraPredictionComponent={
              showManualEntry ? (
                <View style={styles.predictionWithDivider}>
                  <Divider />
                  <Text style={styles.insidePredictionText}>
                    {i18n.t('mix.no_address_match')}{' '}
                    <Text
                      style={styles.manualLink}
                      onPress={() => {
                        setManualEntryMode(true);
                      }}>
                      {i18n.t('mix.enter_manually')}
                    </Text>
                  </Text>
                </View>
              ) : undefined
            }
            noResultComponent={
              showManualEntry ? (
                <Text style={styles.insidePredictionText}>
                  {i18n.t('mix.no_result')}
                  {'. '}
                  <Text
                    style={styles.manualLink}
                    onPress={() => {
                      setManualEntryMode(true);
                    }}>
                    {i18n.t('mix.enter_manually')}
                  </Text>
                </Text>
              ) : undefined
            }
            onFocus={() => {
              setSearch({ term: search.term ?? '', fetchPredictions: true });
              if (search.term?.length === 0) {
                setShowPredictions(false);
                return;
              }
              setShowPredictions(true);
            }}
          />
        </Animated.View>

        <Animated.View style={manualFormStyle}>
          <ManualGooglePlaceForm
            initialData={initialManualData}
            variant={variant}
            onDone={(data) => {
              const result = getManualPlaceDetail(data);
              onChange(result);
              setSearch({ term: result.formattedAddress, fetchPredictions: false });
              setShowPredictions(false);
            }}
          />
        </Animated.View>
      </Animated.View>
      {showError && !!error?.message && <Text style={styles.errorText}>{error.message}</Text>}
      <View style={styles.manualModeContainer}>
        {showManualEntry && !manualEntryMode && (
          <Text style={styles.promptLine}>
            {i18n.t('mix.cant_find_address')}{' '}
            <Text
              style={styles.manualLink}
              onPress={() => {
                setManualEntryMode(true);
              }}>
              {i18n.t('mix.enter_manually')}
            </Text>
          </Text>
        )}
        {showManualEntry && manualEntryMode && (
          <Text style={styles.promptLine}>
            <Text
              style={styles.manualLink}
              onPress={() => {
                setManualEntryMode(false);
              }}>
              {i18n.t('mix.back_to_suggestions')}
            </Text>
          </Text>
        )}
      </View>
    </View>
  );
};

export default GoogleAutoComplete;

const styles = StyleSheet.create({
  errorText: {
    marginTop: 10,
    fontSize: 16,
    color: 'red'
  },

  label: {
    color: 'black',
    marginBottom: 10
  },

  manualModeContainer: {
    marginTop: 12,
    alignItems: 'flex-start'
  },

  promptLine: {
    fontSize: 14,
    verticalAlign: 'middle'
  },

  predictionWithDivider: {
    flexDirection: 'column',
    justifyContent: 'center'
  },

  insidePredictionText: {
    fontSize: 14,
    verticalAlign: 'middle',
    paddingVertical: 10
  },

  manualLink: {
    color: '#0066cc',
    fontWeight: '600',
    textDecorationLine: 'underline',
    verticalAlign: 'middle'
  }
});
